import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Gauge,
  ShieldAlert,
  Siren,
  Store,
  Ticket,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import type { SupportTicket } from '@terra/api-client';
import { Badge, Card, CardBody, EmptyState, Spinner } from '@terra/ui';

import { api } from '../lib/api';

// ── Admin Support Monitoring Console ────────────────────────────────────────
// High-altitude oversight of the marketplace support system:
//   - Open / breached / escalated counts platform-wide
//   - SLA breach list (act-fast queue)
//   - Vendor performance leaderboard (response/resolution times)
//   - Quick escalation action
// Operates on the SAME SupportTicket rows the vendor sees — single source.

const STATUS_TONE: Record<string, 'warning' | 'danger' | 'success' | 'neutral'> = {
  OPEN: 'warning',
  ASSIGNED: 'warning',
  IN_PROGRESS: 'warning',
  WAITING_FOR_CUSTOMER: 'neutral',
  PENDING_CUSTOMER: 'neutral',
  ESCALATED: 'danger',
  RESOLVED: 'success',
  CLOSED: 'neutral',
};

export function SupportMonitoringConsole() {
  const allTickets = useQuery({
    queryKey: ['admin-support-all'],
    queryFn: () => api.support.adminList({ limit: 200 }),
  });
  const breachedTickets = useQuery({
    queryKey: ['admin-support-breached'],
    queryFn: () => api.support.adminList({ slaState: 'BREACHED', limit: 50 }),
  });
  const atRiskTickets = useQuery({
    queryKey: ['admin-support-at-risk'],
    queryFn: () => api.support.adminList({ slaState: 'AT_RISK', limit: 50 }),
  });
  const vendorMetrics = useQuery({
    queryKey: ['admin-support-vendor-metrics'],
    queryFn: () => api.support.adminVendorMetrics(),
  });

  const summary = useMemo(() => {
    const s = { total: 0, open: 0, inProgress: 0, escalated: 0, breached: 0, resolved: 0 };
    for (const t of allTickets.data ?? []) {
      s.total += 1;
      if (t.status === 'OPEN' || t.status === 'ASSIGNED') s.open += 1;
      else if (
        t.status === 'IN_PROGRESS' ||
        t.status === 'WAITING_FOR_CUSTOMER' ||
        t.status === 'PENDING_CUSTOMER'
      )
        s.inProgress += 1;
      if (t.status === 'ESCALATED') s.escalated += 1;
      if (t.slaBreachedAt) s.breached += 1;
      if (t.status === 'RESOLVED' || t.status === 'CLOSED') s.resolved += 1;
    }
    return s;
  }, [allTickets.data]);

  return (
    <div className="space-y-6 pb-10">
      <div className="border-border/60 bg-surface flex items-center justify-between rounded-2xl border p-5 shadow-sm">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">
            Support Monitoring Console
          </h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Real-time view of marketplace support — every ticket, every vendor, every SLA breach.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Open" value={summary.open} icon={Ticket} />
        <StatCard label="In progress" value={summary.inProgress} icon={Gauge} />
        <StatCard label="Escalated" value={summary.escalated} icon={Siren} tone="danger" />
        <StatCard label="SLA breached" value={summary.breached} icon={ShieldAlert} tone="danger" />
        <StatCard label="Resolved" value={summary.resolved} icon={CheckCircle2} tone="success" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <UrgentTicketList
          title="SLA breached"
          icon={ShieldAlert}
          tone="danger"
          tickets={breachedTickets.data ?? []}
          loading={breachedTickets.isLoading}
          empty="No SLA breaches. Vendors are on track."
        />
        <UrgentTicketList
          title="At risk (SLA due in 4h)"
          icon={Clock}
          tone="warning"
          tickets={atRiskTickets.data ?? []}
          loading={atRiskTickets.isLoading}
          empty="No tickets approaching SLA breach."
        />
      </div>

      <VendorLeaderboard rows={vendorMetrics.data ?? []} loading={vendorMetrics.isLoading} />
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  tone?: 'danger' | 'success' | 'warning';
}) {
  const accent =
    tone === 'danger'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : tone === 'success'
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : tone === 'warning'
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-surface-subtle text-ink-muted border-border/60';
  return (
    <div className="border-border/60 bg-surface rounded-2xl border p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className={`grid h-9 w-9 place-items-center rounded-lg border ${accent}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-ink-muted text-[10px] font-extrabold uppercase tracking-widest">
          {label}
        </span>
      </div>
      <p className="text-ink mt-3 text-2xl font-extrabold">{value}</p>
    </div>
  );
}

function UrgentTicketList({
  title,
  icon: Icon,
  tone,
  tickets,
  loading,
  empty,
}: {
  title: string;
  icon: React.ElementType;
  tone: 'danger' | 'warning';
  tickets: SupportTicket[];
  loading: boolean;
  empty: string;
}) {
  const qc = useQueryClient();
  const [escalatingId, setEscalatingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const escalate = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.support.adminEscalate(id, { reason, priority: 'URGENT' }),
    onSuccess: () => {
      setEscalatingId(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin-support-all'] });
      void qc.invalidateQueries({ queryKey: ['admin-support-breached'] });
      void qc.invalidateQueries({ queryKey: ['admin-support-at-risk'] });
    },
  });

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
        <div
          className={`grid h-8 w-8 place-items-center rounded-lg ${
            tone === 'danger' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-ink text-[14px] font-bold">{title}</span>
        <span className="text-ink-muted text-[12px] font-bold">({tickets.length})</span>
      </div>

      <div className="p-0">
        {loading ? (
          <div className="grid min-h-[200px] place-items-center">
            <Spinner />
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={<CheckCircle2 className="h-10 w-10 text-emerald-500" />}
              title="All clear"
              description={empty}
            />
          </div>
        ) : (
          <ul className="divide-border/40 divide-y">
            {tickets.map((t) => (
              <li key={t.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-ink-muted font-mono text-[11px] font-bold">
                        {t.ticketNumber}
                      </span>
                      <Badge tone={STATUS_TONE[t.status] ?? 'neutral'}>
                        {t.status.replace(/_/g, ' ')}
                      </Badge>
                      <Badge tone="warning">{t.priority}</Badge>
                    </div>
                    <p className="text-ink mt-1 truncate text-sm font-semibold">{t.subject}</p>
                    <p className="text-ink-muted mt-0.5 text-[11px]">
                      {t.slaBreachedAt
                        ? `Breached ${format(new Date(t.slaBreachedAt), 'MMM d HH:mm')}`
                        : t.slaDueAt
                          ? `Due ${format(new Date(t.slaDueAt), 'MMM d HH:mm')}`
                          : 'No SLA set'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      escalatingId === t.id ? setEscalatingId(null) : setEscalatingId(t.id)
                    }
                    className="inline-flex h-8 items-center gap-1 rounded-lg bg-rose-50 px-3 text-xs font-bold text-rose-700 hover:bg-rose-100"
                  >
                    <ArrowUpRight className="h-3.5 w-3.5" /> Escalate
                  </button>
                </div>
                {escalatingId === t.id && (
                  <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50/60 p-3">
                    <textarea
                      className="border-border w-full rounded-lg border bg-white px-3 py-2 text-sm"
                      rows={2}
                      placeholder="Escalation reason (e.g. vendor unresponsive 24h)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEscalatingId(null)}
                        className="border-border h-8 rounded-lg border bg-white px-3 text-xs font-bold"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={reason.trim().length < 4 || escalate.isPending}
                        onClick={() => escalate.mutate({ id: t.id, reason })}
                        className="h-8 rounded-lg bg-rose-600 px-3 text-xs font-bold text-white disabled:opacity-50"
                      >
                        Escalate to URGENT
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function VendorLeaderboard({
  rows,
  loading,
}: {
  rows: Array<{
    vendorId: string;
    vendorName: string | null;
    openCount: number;
    inProgressCount: number;
    resolvedCount: number;
    escalatedCount: number;
    slaBreachedCount: number;
    avgFirstResponseMinutes: number | null;
    avgResolutionHours: number | null;
  }>;
  loading: boolean;
}) {
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        // Worst (most breaches, then escalations) first so admin sees problems first.
        if (a.slaBreachedCount !== b.slaBreachedCount)
          return b.slaBreachedCount - a.slaBreachedCount;
        return b.escalatedCount - a.escalatedCount;
      }),
    [rows],
  );
  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
        <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
          <Store className="h-4 w-4" />
        </div>
        <span className="text-ink text-[14px] font-bold">Vendor performance</span>
      </div>
      {loading ? (
        <div className="grid min-h-[200px] place-items-center">
          <Spinner />
        </div>
      ) : sorted.length === 0 ? (
        <div className="p-6">
          <EmptyState
            icon={<Store className="h-10 w-10" />}
            title="No vendor activity yet"
            description="Once vendors start handling tickets, performance metrics will appear here."
          />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                <th className="py-3 pl-5 pr-4">Vendor</th>
                <th className="py-3 pr-4 text-center">Open</th>
                <th className="py-3 pr-4 text-center">In progress</th>
                <th className="py-3 pr-4 text-center">Resolved</th>
                <th className="py-3 pr-4 text-center">Escalated</th>
                <th className="py-3 pr-4 text-center">SLA breaches</th>
                <th className="py-3 pr-4 text-right">Avg first-resp</th>
                <th className="py-3 pr-5 text-right">Avg resolution</th>
              </tr>
            </thead>
            <tbody className="divide-border/40 divide-y text-[13px]">
              {sorted.map((r) => (
                <tr key={r.vendorId} className="hover:bg-surface-subtle/30 transition-colors">
                  <td className="text-ink py-3 pl-5 pr-4 font-semibold">
                    {r.vendorName ?? r.vendorId.slice(0, 8)}
                  </td>
                  <td className="py-3 pr-4 text-center font-mono">{r.openCount}</td>
                  <td className="py-3 pr-4 text-center font-mono">{r.inProgressCount}</td>
                  <td className="py-3 pr-4 text-center font-mono">{r.resolvedCount}</td>
                  <td className="py-3 pr-4 text-center">
                    {r.escalatedCount > 0 ? (
                      <Badge tone="danger">{r.escalatedCount}</Badge>
                    ) : (
                      <span className="text-ink-muted font-mono">0</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-center">
                    {r.slaBreachedCount > 0 ? (
                      <Badge tone="danger">{r.slaBreachedCount}</Badge>
                    ) : (
                      <span className="font-mono text-emerald-600">0</span>
                    )}
                  </td>
                  <td className="text-ink-muted whitespace-nowrap py-3 pr-4 text-right font-mono">
                    {r.avgFirstResponseMinutes != null ? `${r.avgFirstResponseMinutes}m` : '—'}
                  </td>
                  <td className="text-ink-muted whitespace-nowrap py-3 pr-5 text-right font-mono">
                    {r.avgResolutionHours != null ? `${r.avgResolutionHours}h` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SupportMonitoringConsole;
